/**
 * CSV Parser Utilities for User File Upload
 *
 * These functions handle CSV parsing and validation for bulk user creation.
 * Extracted for testability and reusability.
 */

export interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  additionalData?: { [key: string]: string };
  status?: 'valid' | 'error';
  errorMessage?: string;
}

export interface ParsedCSVResult {
  success: boolean;
  users?: UserData[];
  error?: string;
}

export interface HeaderIndices {
  firstNameIndex: number;
  lastNameIndex: number;
  emailIndex: number;
}

/**
 * Email validation regex pattern
 * Matches: local-part@domain.tld
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates an array of user data, checking for:
 * - Required first name
 * - Required last name
 * - Required and valid email format
 * - Duplicate email addresses within the dataset
 *
 * @param users - Array of user data to validate
 * @returns Array of users with status and error messages
 */
export const validateUserData = (users: UserData[]): UserData[] => {
  const emails = new Set<string>();

  return users.map((user) => {
    const errors: string[] = [];

    if (!user.firstName?.trim()) errors.push('First name required');
    if (!user.lastName?.trim()) errors.push('Last name required');
    if (!user.email?.trim()) {
      errors.push('Email required');
    } else if (!EMAIL_REGEX.test(user.email)) {
      errors.push('Invalid email format');
    } else if (emails.has(user.email.toLowerCase())) {
      errors.push('Duplicate email address');
    } else {
      emails.add(user.email.toLowerCase());
    }

    return {
      ...user,
      status: errors.length > 0 ? 'error' : 'valid',
      errorMessage: errors.join('; ')
    };
  });
};

/**
 * Parses CSV text into rows, handling multiline fields inside quotes
 *
 * @param csvText - Raw CSV text content
 * @returns Array of row strings
 */
export const parseCSVRows = (csvText: string): string[] => {
  const rows: string[] = [];
  let currentRow = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];

    if (char === '"') {
      if (inQuotes && i + 1 < csvText.length && csvText[i + 1] === '"') {
        currentRow += '""';
        i++; // Skip next quote
      } else {
        inQuotes = !inQuotes;
        currentRow += char;
      }
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // End of row
      if (currentRow.trim()) {
        rows.push(currentRow);
      }
      currentRow = '';
      // Skip \r\n combinations
      if (char === '\r' && i + 1 < csvText.length && csvText[i + 1] === '\n') {
        i++;
      }
    } else {
      currentRow += char;
    }
  }

  // Add last row if exists
  if (currentRow.trim()) {
    rows.push(currentRow);
  }

  return rows;
};

/**
 * Parses a CSV header line into an array of column names
 * Handles quoted headers and escaped quotes
 *
 * @param line - Header line string
 * @returns Array of header names (lowercase, trimmed)
 */
export const parseHeaderLine = (line: string): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i += 2;
        continue;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
    i++;
  }
  values.push(current.trim());
  return values;
};

/**
 * Parses a single CSV data line into an array of field values
 * Handles quoted fields with escaped quotes
 *
 * @param line - Data line string
 * @returns Array of field values
 */
export const parseCSVLine = (line: string): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        // Escaped quote ("") inside quoted field
        current += '"';
        i += 2; // Skip both quotes
        continue;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator outside quotes
      values.push(current.trim());
      current = '';
    } else {
      // Regular character
      current += char;
    }
    i++;
  }
  values.push(current.trim());
  return values;
};

/**
 * Finds the indices of required columns in the header
 * Uses flexible matching (case-insensitive, partial matching)
 *
 * @param headers - Array of header names (lowercase)
 * @returns Object with column indices, -1 if not found
 */
export const findRequiredColumns = (headers: string[]): HeaderIndices => {
  const lowerHeaders = headers.map(h => h.toLowerCase());

  return {
    firstNameIndex: lowerHeaders.findIndex(h => h.includes('first') && h.includes('name')),
    lastNameIndex: lowerHeaders.findIndex(h => h.includes('last') && h.includes('name')),
    emailIndex: lowerHeaders.findIndex(h => h.includes('email'))
  };
};

/**
 * Parses a complete CSV file and extracts user data
 *
 * @param csvText - Raw CSV text content
 * @returns ParsedCSVResult with success status and users or error
 */
export const parseCSVFile = (csvText: string): ParsedCSVResult => {
  const lines = parseCSVRows(csvText);

  if (lines.length < 2) {
    return {
      success: false,
      error: 'File must contain at least a header row and one data row'
    };
  }

  const headers = parseHeaderLine(lines[0]).map(h => h.toLowerCase());
  const { firstNameIndex, lastNameIndex, emailIndex } = findRequiredColumns(headers);

  if (firstNameIndex === -1 || lastNameIndex === -1 || emailIndex === -1) {
    return {
      success: false,
      error: 'CSV must contain columns: first_name (or First Name), last_name (or Last Name), email'
    };
  }

  const parsedUsers: UserData[] = lines.slice(1)
    .map(line => {
      const values = parseCSVLine(line);

      const additionalData: { [key: string]: string } = {};
      headers.forEach((header, idx) => {
        if (idx !== firstNameIndex && idx !== lastNameIndex && idx !== emailIndex && values[idx]?.trim()) {
          additionalData[header] = values[idx];
        }
      });

      return {
        firstName: values[firstNameIndex] || '',
        lastName: values[lastNameIndex] || '',
        email: values[emailIndex] || '',
        additionalData: Object.keys(additionalData).length > 0 ? additionalData : undefined
      };
    })
    .filter(user => user.firstName || user.lastName || user.email);

  return {
    success: true,
    users: parsedUsers
  };
};

/**
 * Complete CSV processing: parse and validate
 *
 * @param csvText - Raw CSV text content
 * @returns ParsedCSVResult with validated users
 */
export const processCSVContent = (csvText: string): ParsedCSVResult => {
  const parseResult = parseCSVFile(csvText);

  if (!parseResult.success || !parseResult.users) {
    return parseResult;
  }

  const validatedUsers = validateUserData(parseResult.users);

  return {
    success: true,
    users: validatedUsers
  };
};
